import clsx from 'clsx';
import { useEffect, useState } from 'react';
import {
  ButtonColor,
  ButtonVariant,
  Dialog,
  FolderTree,
  Spinner,
  TextButton,
  TextInput,
  Typography,
} from '../../../components';
import { useGetFolderPath } from '../../../hooks';
import { useGetFoldersQuery } from '../../../services';
import { ComponentSize } from '../../../types';
import CreateFolderButton from './CreateFolderButton';
import SaveFolderButton from './SaveFolderButton';

interface BaseManageFolderModalProps {
  folderParentId?: string | null;
  closeModal: () => void;
}

interface EditFolderModalProps extends BaseManageFolderModalProps {
  folderId: string;
  folderName: string;
  onMoveSuccess: () => void;
}

interface CreateFolderModalProps extends BaseManageFolderModalProps {
  folderId?: never;
  folderName?: never;
  onMoveSuccess?: never;
}

type ManageFolderModalProps = EditFolderModalProps | CreateFolderModalProps;

const ManageFolderModal = ({
  folderId,
  folderName,
  folderParentId,
  closeModal,
  onMoveSuccess,
}: ManageFolderModalProps) => {
  // Stores the path of folder IDs leading to the current folder.
  const [folderPathIds, setFolderPathIds] = useState<string[]>([]);
  // The current folder's name.
  const [nameInput, setNameInput] = useState(folderName || '');
  // The parent folder in which the new folder will be created or moved.
  // If null, the new folder will be at the root level.
  const [selectedFolderId, setSelectedFolderId] = useState(folderParentId || null);

  const { data: folderHierarchy, isLoading: isFoldersLoading } = useGetFoldersQuery({
    all: true,
    includeProspects: false,
  });

  const folders = folderHierarchy?.folders || [];
  const getFolderPath = useGetFolderPath();

  useEffect(() => {
    // If the folder is being created, we get the path to the parent folder.
    // If the folder is being edited, we get the path to the current folder.
    const id = folderId ?? folderParentId;
    if (!id || !folders.length) return;

    // Get the path of folders leading to the current folder.
    const path = getFolderPath(folders, id) || [];
    const pathIds = path.map(({ id }) => id);
    setFolderPathIds(pathIds);
  }, [folderId, folderParentId, folders, getFolderPath]);

  return (
    <Dialog isOpen onClose={closeModal} title={(folderId ? 'Edit' : 'Create') + ' folder'}>
      <div className="flex h-full flex-col">
        {isFoldersLoading && <Spinner className="h-32 self-center" size={ComponentSize.MEDIUM} />}
        <div className={clsx('flex h-full flex-col justify-between gap-4', isFoldersLoading && 'hidden')}>
          <div className="flex flex-col gap-4 overflow-auto">
            <div className="flex flex-col gap-2">
              <Typography>Folder name</Typography>
              <TextInput
                placeholder="Add folder name"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Typography>Location</Typography>
              <FolderTree
                folders={folders}
                openFolderIds={folderPathIds}
                setSelectedFolderId={setSelectedFolderId}
                selectedFolderId={selectedFolderId}
              />
            </div>
          </div>
          <div className="ml-auto flex gap-2">
            <TextButton color={ButtonColor.NEUTRAL} text="Cancel" variant={ButtonVariant.SOFT} onClick={closeModal} />
            {folderId && (
              <SaveFolderButton
                folderId={folderId}
                folderName={folderName}
                folderParentId={selectedFolderId}
                nameInput={nameInput}
                selectedFolderId={selectedFolderId}
                closeModal={closeModal}
                onMoveSuccess={onMoveSuccess}
              />
            )}
            {!folderId && (
              <CreateFolderButton nameInput={nameInput} selectedFolderId={selectedFolderId} closeModal={closeModal} />
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ManageFolderModal;
